import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import ComboBoxItem from "components/ComboBox/ComboBoxItem";

export interface FormErrors {
  account?: {
    email?: string;
    username?: string;
    password?: string;
    confirmPassword?: string;
  };
  profile?: {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    company?: string;
    industry?: string;
    role?: string;
  };
  preferences?: {
    outreach?: string;
    outreachOthers?: string;
    currentSolution?: string;
    currentSolutionOthers?: string;
    keyChallenges?: string;
    keyChallengesOthers?: string;
    teamSize?: string;
    referralCode?: string;
  };
}

export interface SignUpState {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  company: string;
  industry?: ComboBoxItem<string>;
  role?: ComboBoxItem<string>;
  outreach?: ComboBoxItem<string>;
  outreachOthers: string;
  currentSolution?: ComboBoxItem<string>;
  currentSolutionOthers: string;
  keyChallenges?: ComboBoxItem<string>;
  keyChallengesOthers: string;
  teamSize?: ComboBoxItem<string>;
  referralCode: string;
  errors?: FormErrors;
}

const initialState: SignUpState = {
  username: "",
  email: "",
  password: "",
  confirmPassword: "",
  phoneNumber: "",
  firstName: "",
  lastName: "",
  company: "",
  industry: undefined,
  role: undefined,
  outreach: undefined,
  outreachOthers: "",
  currentSolution: undefined,
  currentSolutionOthers: "",
  keyChallenges: undefined,
  keyChallengesOthers: "",
  teamSize: undefined,
  referralCode: "",
  errors: undefined,
};

const signUpSlice = createSlice({
  name: "signUp",
  initialState,
  reducers: {
    setUsername: (state, action: PayloadAction<string>) => {
      state.username = action.payload;
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setPassword: (state, action: PayloadAction<string>) => {
      state.password = action.payload;
    },
    setConfirmPassword: (state, action: PayloadAction<string>) => {
      state.confirmPassword = action.payload;
    },
    setFirstName: (state, action: PayloadAction<string>) => {
      state.firstName = action.payload;
    },
    setLastName: (state, action: PayloadAction<string>) => {
      state.lastName = action.payload;
    },
    setPhoneNumber: (state, action: PayloadAction<string>) => {
      state.phoneNumber = action.payload;
    },
    setCompany: (state, action: PayloadAction<string>) => {
      state.company = action.payload;
    },
    setIndustry: (state, action: PayloadAction<ComboBoxItem<string>>) => {
      state.industry = action.payload;
    },
    setRole: (state, action: PayloadAction<ComboBoxItem<string>>) => {
      state.role = action.payload;
    },
    setOutreach: (state, action: PayloadAction<ComboBoxItem<string>>) => {
      state.outreach = action.payload;
    },
    setOutreachOthers: (state, action: PayloadAction<string>) => {
      state.outreachOthers = action.payload;
    },
    setCurrentSolution: (
      state,
      action: PayloadAction<ComboBoxItem<string>>,
    ) => {
      state.currentSolution = action.payload;
    },
    setCurrentSolutionOthers: (state, action: PayloadAction<string>) => {
      state.currentSolutionOthers = action.payload;
    },
    setKeyChallenges: (state, action: PayloadAction<ComboBoxItem<string>>) => {
      state.keyChallenges = action.payload;
    },
    setKeyChallengesOthers: (state, action: PayloadAction<string>) => {
      state.keyChallengesOthers = action.payload;
    },
    setTeamSize: (state, action: PayloadAction<ComboBoxItem<string>>) => {
      state.teamSize = action.payload;
    },
    setReferralCode: (state, action: PayloadAction<string>) => {
      state.referralCode = action.payload;
    },
    setFormErrors: (state, action: PayloadAction<FormErrors>) => {
      state.errors = action.payload;
    },
    reset: (state) => {
      state.username = "";
      state.email = "";
      state.password = "";
      state.confirmPassword = "";
      state.firstName = "";
      state.lastName = "";
      state.phoneNumber = "";
      state.company = "";
      state.industry = undefined;
      state.role = undefined;
      state.outreach = undefined;
      state.outreachOthers = "";
      state.currentSolution = undefined;
      state.currentSolutionOthers = "";
      state.keyChallenges = undefined;
      state.keyChallengesOthers = "";
      state.teamSize = undefined;
      state.errors = undefined;
    },
  },
});

export const {
  setUsername,
  setEmail,
  setPassword,
  setConfirmPassword,
  setFirstName,
  setLastName,
  setPhoneNumber,
  setCompany,
  setIndustry,
  setRole,
  setOutreach,
  setOutreachOthers,
  setCurrentSolution,
  setCurrentSolutionOthers,
  setKeyChallenges,
  setKeyChallengesOthers,
  setTeamSize,
  setReferralCode,
  setFormErrors,
  reset,
} = signUpSlice.actions;

export default signUpSlice.reducer;
